import { Alert, Box, Button } from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { BulkOverrideConfirm, FormRowSwitch, MyModal } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BoxModel } from "types";

interface BulkManageBoxesProps {
  boxes: BoxModel[];
  onSuccess: () => void;
  onClose: () => void;
}

function allBoxesMatch(boxes: BoxModel[]) {
  if (!boxes.length) return false;
  const first = Boolean(boxes[0].recordVideo);
  return boxes.every((box) => Boolean(box.recordVideo) === first);
}

export default function BulkManageBoxes({ boxes, onSuccess, onClose }: BulkManageBoxesProps) {
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recordVideo, setRecordVideo] = useState(() =>
    allBoxesMatch(boxes) ? Boolean(boxes[0].recordVideo) : false
  );
  const { isLoading, response, error, callFetch } = useFetch<{ updated: string[]; failed: { id: string }[] }>("PUT");

  const mixed = useMemo(() => boxes.length >= 2 && !allBoxesMatch(boxes), [boxes]);

  const onConfirmSave = useCallback(() => {
    callFetch(ENDPOINTS.boxes + "/bulk", {
      body: JSON.stringify({
        ids: boxes.map((b) => b._id),
        recordVideo
      })
    }).catch(console.error);
  }, [callFetch, boxes, recordVideo]);

  useEffect(() => {
    if (response) {
      const failed = response.data?.failed || [];
      enqueueSnackbar(response.message || "", { variant: failed.length ? "warning" : "success" });
      if (failed.length) {
        const names = failed
          .map((f) => boxes.find((b) => b._id === f.id)?.deviceId || f.id)
          .join(", ");
        enqueueSnackbar(`Failed: ${names}`, { variant: "error" });
      }
      setConfirmOpen(false);
      onSuccess();
      onClose();
    }
  }, [response, enqueueSnackbar, onSuccess, onClose, boxes]);

  useEffect(() => {
    if (error) enqueueSnackbar(error.message || "", { variant: "error" });
  }, [error, enqueueSnackbar]);

  return (
    <>
      <MyModal
        title={`${t["Manage Recording"] || "Manage Recording"} — ${boxes.length} ${
          boxes.length === 1 ? "Box" : "Boxes"
        }`}
        subtitle={
          t["Configure recording for the selected boxes."] || "Configure recording for the selected boxes."
        }
        open={true}
        setOpen={onClose}
        variant="light"
      >
        {mixed ? (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            {t["Selected boxes currently use different settings. Saving will override all of them with the values below."] ||
              "Selected boxes currently use different settings. Saving will override all of them with the values below."}
          </Alert>
        ) : null}
        <FormRowSwitch
          label={t["Record Video"] || "Record Video"}
          checked={recordVideo}
          onChange={setRecordVideo}
        />
        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              minWidth: 120,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              borderColor: COLORS.surface.border,
              color: COLORS.surface.primaryText,
              fontWeight: 500,
              textTransform: "none"
            }}
          >
            {t["Cancel"]}
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={isLoading}
            onClick={() => setConfirmOpen(true)}
            sx={{
              minWidth: 140,
              height: 44,
              borderRadius: `${COLORS.radius.md}px`,
              fontWeight: 500,
              textTransform: "none",
              ...primaryContainedButtonSx
            }}
          >
            {t["Save Changes"] || "Save Changes"}
          </Button>
        </Box>
      </MyModal>

      <BulkOverrideConfirm
        open={confirmOpen}
        count={boxes.length}
        entityLabel={boxes.length === 1 ? "box" : "boxes"}
        mixed={mixed}
        isLoading={isLoading}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={onConfirmSave}
      />
    </>
  );
}
